/**
 * RelecteursPanel — adresses de relecture du service technique (item 3.7).
 *
 * La commune enregistre ici ses relecteurs (juriste de l'intercommunalité,
 * secrétaire de mairie, élu référent…). Ils n'ont PAS besoin d'un compte VPC :
 * on stocke un simple couple nom + email. L'agent en choisit un au moment
 * d'envoyer un arrêté à relire.
 *
 * Désactiver plutôt que supprimer : un relecteur retiré de la liste reste
 * cohérent avec les envois déjà tracés sur les arrêtés passés.
 */
import { useCallback, useEffect, useState } from 'react';
import { api, ApiException } from '@/lib/api';
import { Button, Card, Badge } from '@/components/ui';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { Plus, Trash2, Mail, UserCheck } from 'lucide-react';
import type { StRelecteur } from '@roronoa-collectivites/shared-types';

const INPUT_CLS =
  'h-11 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-3 text-ink ' +
  'placeholder:text-ink-dim shadow-soft transition-all focus:border-tech focus:outline-none ' +
  'focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong';

export function RelecteursPanel({ editable = true }: { editable?: boolean }) {
  const { confirm, dialogElement } = useConfirmDialog();
  const [items, setItems] = useState<StRelecteur[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ouvert, setOuvert] = useState(false);
  const [saving, setSaving] = useState(false);

  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [fonction, setFonction] = useState('');

  const charger = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.serviceTechnique.arretes.relecteurs.list(true);
      setItems(res.items);
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Chargement impossible');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { void charger(); }, [charger]);

  async function ajouter() {
    if (nom.trim().length < 2 || !email.trim()) {
      setError('Renseignez au moins un nom et une adresse email.');
      return;
    }
    setSaving(true); setError(null);
    try {
      await api.serviceTechnique.arretes.relecteurs.create({
        nom: nom.trim(), email: email.trim(),
        fonction: fonction.trim() || null,
      });
      setNom(''); setEmail(''); setFonction(''); setOuvert(false);
      await charger();
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Ajout impossible');
    } finally { setSaving(false); }
  }

  async function basculerActif(r: StRelecteur) {
    setError(null);
    try {
      await api.serviceTechnique.arretes.relecteurs.update(r.id, {
        nom: r.nom, email: r.email, fonction: r.fonction, actif: !r.actif,
      });
      await charger();
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Modification impossible');
    }
  }

  async function supprimer(r: StRelecteur) {
    const ok = await confirm({
      title: 'Supprimer ce relecteur ?',
      description: `${r.nom} ne pourra plus être sélectionné. Si des arrêtés lui ont déjà été envoyés, préférez le désactiver pour garder l'historique lisible.`,
      confirmLabel: 'Supprimer',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.serviceTechnique.arretes.relecteurs.remove(r.id);
      await charger();
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Suppression impossible');
    }
  }

  return (
    <Card variant="glass" padded>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-ink">Adresses de relecture</h3>
          <p className="text-xs text-ink-subtle">
            Personnes sollicitées pour relire un arrêté. Un compte VPC n'est pas nécessaire.
          </p>
        </div>
        {editable && !ouvert && (
          <Button variant="secondary" size="sm" onClick={() => setOuvert(true)}>
            <Plus className="h-4 w-4" aria-hidden="true" /> Ajouter
          </Button>
        )}
      </div>

      {error && <p className="mb-2 text-sm text-danger">{error}</p>}

      {ouvert && (
        <div className="mb-4 grid gap-3 rounded-r-md border border-border bg-surface/40 p-3 sm:grid-cols-3">
          <label className="text-sm">
            <span className="mb-1 block text-xs text-ink-muted">Nom</span>
            <input value={nom} onChange={(e) => setNom(e.target.value)} maxLength={150} className={INPUT_CLS} />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-xs text-ink-muted">Email</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={255} className={INPUT_CLS} />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-xs text-ink-muted">Fonction (optionnel)</span>
            <input
              value={fonction} onChange={(e) => setFonction(e.target.value)} maxLength={150}
              placeholder="Juriste CdC…" className={INPUT_CLS}
            />
          </label>
          <div className="flex gap-2 sm:col-span-3">
            <Button variant="primary" size="sm" disabled={saving} onClick={() => void ajouter()}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => { setOuvert(false); setError(null); }}>
              Annuler
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="py-4 text-center text-sm text-ink-subtle">Chargement…</p>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-6 text-center">
          <Mail className="h-7 w-7 text-ink-dim" aria-hidden="true" />
          <p className="text-sm text-ink-muted">Aucune adresse de relecture enregistrée.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((r) => (
            <div
              key={r.id}
              className="flex items-center gap-3 rounded-r-md border border-border bg-surface/50 px-3 py-2"
            >
              <UserCheck className="h-4 w-4 shrink-0 text-tech-fg" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium text-ink">{r.nom}</span>
                  {!r.actif && <Badge variant="neutral" size="sm">Inactif</Badge>}
                </div>
                <p className="truncate text-xs text-ink-subtle">
                  {r.email}{r.fonction ? ` · ${r.fonction}` : ''}
                </p>
              </div>
              {editable && (
                <div className="flex shrink-0 gap-1">
                  <Button variant="ghost" size="sm" onClick={() => void basculerActif(r)}>
                    {r.actif ? 'Désactiver' : 'Réactiver'}
                  </Button>
                  <button
                    type="button"
                    onClick={() => void supprimer(r)}
                    aria-label={`Supprimer ${r.nom}`}
                    className="rounded-r-sm p-1.5 text-ink-muted transition-colors hover:text-danger"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {dialogElement}
    </Card>
  );
}
